import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { Shield, Building2, FileText, AlertTriangle, CheckCircle2, Clock, RefreshCw, Ticket as TicketIcon } from "lucide-react";

const KPI_DEFS = [
    { key: "total", label: "Total (90d)", icon: TicketIcon, color: "var(--accent)" },
    { key: "open", label: "Abiertos", icon: Clock, color: "var(--pill-info-fg)" },
    { key: "pending", label: "En progreso", icon: Clock, color: "var(--pill-warning-fg)" },
    { key: "resolved", label: "Resueltos", icon: CheckCircle2, color: "var(--pill-success-fg)" },
    { key: "closed", label: "Cerrados", icon: CheckCircle2, color: "var(--text-muted)" },
    { key: "sla_breached", label: "SLA roto", icon: AlertTriangle, color: "var(--pill-danger-fg)" },
];

export default function InternalDashboard() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const load = async () => {
        setLoading(true); setError("");
        try {
            const { data } = await api.get("/internal/dashboard");
            setData(data);
        } catch (err) {
            setError(formatApiError(err, "No pudimos cargar el dashboard interno."));
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    if (!user?.is_internal_use) {
        return (
            <div className="card card-padded" data-testid="internal-dashboard-locked">
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                    <Shield size={22} style={{ color: "var(--pill-danger-fg)" }} />
                    <h3 style={{ margin: 0 }}>Acceso restringido</h3>
                </div>
                <p className="text-muted" style={{ fontSize: 14, margin: 0 }}>
                    Esta sección está reservada al perímetro <b>Uso Interno</b>. Habla con un superadmin para que active el flag en tu perfil.
                </p>
            </div>
        );
    }

    return (
        <div data-testid="internal-dashboard-page">
            <div className="page-head">
                <div>
                    <h1>
                        <Shield size={20} style={{ marginRight: 8, verticalAlign: "-3px", color: "var(--pill-warning-fg)" }} />
                        Mundo Interno
                    </h1>
                    <p>KPIs y operación del perímetro Uso Interno (últimos 90 días). Visible solo para personas con el flag activado.</p>
                </div>
                <button className="btn" onClick={load} disabled={loading} data-testid="internal-dashboard-refresh" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <RefreshCw size={14} className={loading ? "spin" : ""} /> Actualizar
                </button>
            </div>

            {error && <div className="alert alert-error" data-testid="internal-dashboard-error">{error}</div>}

            {data && (
                <>
                    {/* KPI strip */}
                    <div className="card" data-testid="internal-dashboard-kpis" style={{ marginBottom: 18 }}>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, padding: 16 }}>
                            {KPI_DEFS.map(({ key, label, icon: Icon, color }) => (
                                <div key={key} data-testid={`internal-kpi-${key}`} style={{ padding: 14, background: "var(--bg-2)", border: "1px solid var(--border)", borderRadius: 8 }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--text-muted)", fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em" }}>
                                        <Icon size={12} style={{ color }} /> {label}
                                    </div>
                                    <div style={{ fontSize: 26, fontWeight: 700, color: "var(--text)", marginTop: 6 }}>
                                        {data.kpis[key] || 0}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div style={{ display: "flex", gap: 18, padding: "0 16px 14px", color: "var(--text-muted)", fontSize: 12 }}>
                            <div><Building2 size={12} style={{ marginRight: 4, verticalAlign: "-2px" }} /> {data.companies_count} empresas internas</div>
                            <div><FileText size={12} style={{ marginRight: 4, verticalAlign: "-2px" }} /> {data.templates_count} tipos de solicitud internos</div>
                        </div>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginBottom: 18 }}>
                        {/* Top empresas internas */}
                        <div className="card" data-testid="internal-by-company">
                            <div className="card-header"><h3>Empresas internas con más tickets</h3></div>
                            <div style={{ padding: "8px 16px 16px" }}>
                                {data.by_company.length === 0 && <div className="text-muted" style={{ fontSize: 13, padding: 16 }}>Sin tickets internos en los últimos 90 días.</div>}
                                {data.by_company.map((c, idx) => {
                                    const max = data.by_company[0]?.count || 1;
                                    const pct = Math.round((c.count / max) * 100);
                                    return (
                                        <div key={c.company_id} data-testid={`internal-company-row-${idx}`} style={{ marginBottom: 10 }}>
                                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 4 }}>
                                                <span style={{ color: "var(--text)" }}>{c.name}</span>
                                                <span className="mono" style={{ color: "var(--text-muted)" }}>{c.count}</span>
                                            </div>
                                            <div style={{ height: 6, background: "var(--bg-2)", borderRadius: 3, overflow: "hidden" }}>
                                                <div style={{ width: `${pct}%`, height: "100%", background: "var(--accent)" }} />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Top tipos de solicitud internos */}
                        <div className="card" data-testid="internal-by-template">
                            <div className="card-header"><h3>Tipos de solicitud internos más usados</h3></div>
                            <div style={{ padding: "8px 16px 16px" }}>
                                {data.by_template.length === 0 && <div className="text-muted" style={{ fontSize: 13, padding: 16 }}>Sin tickets levantados desde tipos internos aún.</div>}
                                {data.by_template.map((t, idx) => {
                                    const max = data.by_template[0]?.count || 1;
                                    const pct = Math.round((t.count / max) * 100);
                                    return (
                                        <div key={t.template_id} data-testid={`internal-template-row-${idx}`} style={{ marginBottom: 10 }}>
                                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 4 }}>
                                                <span style={{ color: "var(--text)" }}>{t.title}</span>
                                                <span className="mono" style={{ color: "var(--text-muted)" }}>{t.count}</span>
                                            </div>
                                            <div style={{ height: 6, background: "var(--bg-2)", borderRadius: 3, overflow: "hidden" }}>
                                                <div style={{ width: `${pct}%`, height: "100%", background: "var(--pill-warning-fg)" }} />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Últimos tickets internos */}
                    <div className="card" data-testid="internal-recent-tickets">
                        <div className="card-header"><h3>Últimos tickets del perímetro interno</h3></div>
                        <div className="table-wrap">
                            <table className="t">
                                <thead><tr>
                                    <th>Ticket</th><th>Asunto</th><th>Empresa</th><th>Solicitante</th><th>Estado</th><th>Prioridad</th>
                                </tr></thead>
                                <tbody>
                                    {data.recent_tickets.length === 0 && (
                                        <tr><td colSpan={6} className="text-muted" style={{ textAlign: "center", padding: 30 }}>Sin tickets internos todavía.</td></tr>
                                    )}
                                    {data.recent_tickets.map((t) => (
                                        <tr key={t.ticket_id} data-testid={`internal-recent-${t.ticket_id}`}>
                                            <td><Link to={`/tickets/${t.ticket_id}`} className="linklike">{t.ticket_id}</Link></td>
                                            <td style={{ maxWidth: 340, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.subject}</td>
                                            <td className="text-muted">{t.company_name}</td>
                                            <td className="text-muted text-sm">{t.requester_name || t.requester_email}</td>
                                            <td><span className="badge">{t.status}</span></td>
                                            <td><span className="badge">{t.priority}</span></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
